import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { useQuery } from '@apollo/client';

import { ModalModel, ModalContext } from '@notino/react-styleguide';
import {
  CatalogVariantFragmentFragment,
  GetCustomerBenefitsQuery,
} from '@notino/shared/definitions/types';
import { resetDataLayer } from '@notino/web-tracking';

import {
  TrackingContextProvider,
  useTrackingContext,
} from '@context/tracking/TrackingContext';

import { useNewPdDesignEnabled } from '../../hooks/useNewPdDesignEnabled';
import { trackPageReload } from '../../tracking';

import { BenefitsModal } from './components/BenefitsModal';
import { HowToLoyaltyModal } from './components/HowToLoyaltyDiscountModal';
import { messages } from './messages';
import getCustomerBenefitsQuery from './queries/getCustomerBenefits.graphql';
import {
  CustomerDiscountBoldWrapper,
  CustomerDiscountWrapper,
  LoyaltyDiscountStyledWrapper,
  StyledDiamondIcon,
} from './styled';
import { trackLoyaltyModalClose } from './trackLoyaltyModal';
import { getDiscountWithSymbol, subjectTypeMessages } from './utils';

interface ILoyaltyDiscount {
  customerBenefits: CatalogVariantFragmentFragment['features']['customerBenefits'];
  // We need these props to track pageView of PD after modal close
  gtmPageReloadProps: Parameters<typeof trackPageReload>[0];
}

export const LoyaltyDiscount: React.FC<ILoyaltyDiscount> = ({
  customerBenefits,
  gtmPageReloadProps,
}) => {
  const newDesign = useNewPdDesignEnabled();
  const { data, loading } = useQuery<GetCustomerBenefitsQuery>(
    getCustomerBenefitsQuery,
    {
      ssr: false,
    }
  );

  const { getTimeFromInit } = useTrackingContext();
  const { toggleModal, hideModal } = ModalContext.useModalContext();
  const modalLastClosedMs = React.useRef(0);
  TrackingContextProvider.useSubscribeToInitTimeReset(() => {
    modalLastClosedMs.current = 0;
  });

  const handleClick = () => {
    const hasBenefits = data?.customerBenefits?.hasActiveBenefits;
    const timing = getTimeFromInit() - modalLastClosedMs.current;
    toggleModal(
      hasBenefits ? (
        <BenefitsModal timing={timing} />
      ) : (
        <HowToLoyaltyModal timing={timing} />
      ),
      {
        noBorders: true,
        size: ModalModel.Sizes.large,
        showOverflow: true,
        positionBottomOnMobile: true,
        closeIconColor: !hasBenefits
          ? { xs: 'icon.inverse', md: 'icon.tertiary' }
          : undefined,
        onClose: () => {
          trackLoyaltyModalClose();
          modalLastClosedMs.current = getTimeFromInit();
          resetDataLayer();
          trackPageReload(gtmPageReloadProps);
          hideModal();
        },
      }
    );
  };

  const formattedBenefits = React.useMemo(
    () => ({
      availableDiscount: customerBenefits.availableDiscount && {
        value: getDiscountWithSymbol(
          customerBenefits.availableDiscount.discountType,
          customerBenefits.availableDiscount.value
        ),
      },
      customerDiscount: customerBenefits.customerDiscount && {
        value: getDiscountWithSymbol(
          customerBenefits.customerDiscount.discountType,
          customerBenefits.customerDiscount.value
        ),
      },
    }),
    [customerBenefits.availableDiscount, customerBenefits.customerDiscount]
  );

  if (
    !formattedBenefits.availableDiscount &&
    !formattedBenefits.customerDiscount
  ) {
    return null;
  }

  const isCustomerDiscount = Boolean(formattedBenefits.customerDiscount);

  return (
    <LoyaltyDiscountStyledWrapper
      onClick={handleClick}
      newDesign={newDesign}
      isCustomerDiscount={isCustomerDiscount}
      disabled={loading}
    >
      <>
        <StyledDiamondIcon width="24px" height="24px" />
        {customerBenefits.availableDiscount && (
          <FormattedMessage
            {...messages.possibleDiscount}
            values={{ value: formattedBenefits.availableDiscount.value }}
          />
        )}
        {customerBenefits.customerDiscount && (
          <span>
            <FormattedMessage
              {...messages.customerDiscount}
              values={{
                discountSubject:
                  customerBenefits.customerDiscount.discountSubject,
                subjectType: (
                  <FormattedMessage
                    {...subjectTypeMessages[
                      customerBenefits.customerDiscount.subjectType
                    ]}
                  />
                ),
                customerDiscountBold: (
                  <CustomerDiscountBoldWrapper>
                    <FormattedMessage
                      {...messages.customerDiscountBold}
                      values={{
                        value: formattedBenefits.customerDiscount.value,
                      }}
                    />
                  </CustomerDiscountBoldWrapper>
                ),
                more: (
                  <CustomerDiscountWrapper>
                    <FormattedMessage {...messages.moreDiscountInfo} />
                  </CustomerDiscountWrapper>
                ),
              }}
            />
          </span>
        )}
      </>
    </LoyaltyDiscountStyledWrapper>
  );
};
